import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { BackChevron } from '../components/BackChevron';
import {
  ApiError,
  getRecommendations,
  type RecommendItinerary,
  type RecommendRequest,
} from '../lib/api-client';
import { pickI18n } from '../lib/format';
import { useI18n } from '../lib/i18n-context';

/**
 * C-1 智慧行程推薦頁 — AC-C1-1 / AC-C1-2 / AC-C1-3。
 *
 * 現場遊客輸入「停留時間 / 預算 / 人數 / 興趣」，POST /recommendations 取得
 * 行程組合建議，每組顯示商品組合 + 預估時間 + 總價，可一鍵帶入購票流程。
 *
 * 雙入口（AC-C1-1）：觸控表單為主要入口；語音入口屬 C-3 語音模組範圍
 * （供應商契約未簽，見 docs/AC_traceability.csv AC-C3-7），本頁僅放 scaffold
 * 按鈕並提示改用觸控，不實際收音。
 */

/** 興趣分類選項（對映 products.category）。 */
const INTEREST_OPTIONS: { value: string; label: string }[] = [
  { value: 'trip', label: '遊程' },
  { value: 'experience', label: '體驗' },
  { value: 'package', label: '套裝行程' },
  { value: 'charter', label: '包船' },
];

/** 停留時間快捷選項（分鐘）。 */
const STAY_PRESETS = [60, 120, 180, 240];

export function RecommendationsPage(): JSX.Element {
  const navigate = useNavigate();
  const { locale } = useI18n();

  const [stayMinutes, setStayMinutes] = useState<number>(120);
  const [budget, setBudget] = useState<string>('');
  const [adults, setAdults] = useState<number>(2);
  const [children, setChildren] = useState<number>(0);
  const [interests, setInterests] = useState<string[]>([]);
  const [voiceHintShown, setVoiceHintShown] = useState<boolean>(false);

  const recommendMutation = useMutation({
    mutationFn: () => {
      const payload: RecommendRequest = {
        stay_minutes: stayMinutes,
        party_adults: adults,
        party_children: children,
      };
      const b = Number(budget);
      if (budget.trim() !== '' && Number.isFinite(b) && b > 0) payload.budget = b;
      if (interests.length > 0) payload.interests = interests;
      return getRecommendations(payload);
    },
  });

  function toggleInterest(value: string): void {
    setInterests((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  }

  /** AC-C1-3：一鍵帶入購票——以推薦行程之首個商品進入購買流程。 */
  function buyItinerary(itin: RecommendItinerary): void {
    const first = itin.products[0];
    if (first) navigate(`/products/${first.product_id}/buy`);
  }

  const result = recommendMutation.data;

  return (
    <div className="app-shell">
      <header className="topbar topbar-lead">
        <BackChevron to="/" label="返回" />
        <h1>智慧行程推薦</h1>
      </header>

      <p style={{ color: '#555', margin: '4px 0 12px', fontSize: 14 }}>
        告訴我們您的條件，系統會推薦適合的行程組合。
      </p>

      {/* AC-C1-1：雙入口——觸控表單 + 語音入口 scaffold */}
      <section className="card" data-testid="rec-form">
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 14, fontWeight: 600 }}>停留時間</label>
          <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
            {STAY_PRESETS.map((m) => (
              <button
                key={m}
                type="button"
                data-testid={`rec-stay-${m}`}
                onClick={() => setStayMinutes(m)}
                style={{
                  padding: '8px 14px',
                  borderRadius: 999,
                  border: stayMinutes === m ? '2px solid #0E5A8C' : '1px solid #d1d5db',
                  background: stayMinutes === m ? '#e8f1f8' : 'white',
                  fontWeight: stayMinutes === m ? 600 : 400,
                  cursor: 'pointer',
                }}
              >
                {m >= 60 ? `${m / 60} 小時` : `${m} 分鐘`}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label htmlFor="rec-budget" style={{ fontSize: 14, fontWeight: 600 }}>
            預算上限（整團，選填）
          </label>
          <input
            id="rec-budget"
            data-testid="rec-budget"
            type="number"
            inputMode="numeric"
            min={0}
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            placeholder="不限"
            style={{
              display: 'block',
              marginTop: 6,
              padding: '8px 10px',
              width: 160,
              fontSize: 15,
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
          <Counter
            label="成人"
            testId="rec-adults"
            value={adults}
            min={1}
            max={20}
            onChange={setAdults}
          />
          <Counter
            label="兒童"
            testId="rec-children"
            value={children}
            min={0}
            max={20}
            onChange={setChildren}
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 14, fontWeight: 600 }}>興趣（選填，可複選）</label>
          <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
            {INTEREST_OPTIONS.map((opt) => {
              const on = interests.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`rec-interest-${opt.value}`}
                  aria-pressed={on}
                  onClick={() => toggleInterest(opt.value)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 999,
                    border: on ? '2px solid #0E5A8C' : '1px solid #d1d5db',
                    background: on ? '#e8f1f8' : 'white',
                    fontWeight: on ? 600 : 400,
                    cursor: 'pointer',
                  }}
                >
                  {on ? '✓ ' : ''}
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          className="btn-primary btn-block"
          data-testid="rec-submit"
          disabled={recommendMutation.isPending}
          onClick={() => recommendMutation.mutate()}
        >
          {recommendMutation.isPending ? '推薦中…' : '取得行程推薦'}
        </button>

        <hr className="divider" />
        <button
          type="button"
          data-testid="rec-voice-entry"
          onClick={() => setVoiceHintShown(true)}
          style={{
            width: '100%',
            padding: '10px 14px',
            borderRadius: 8,
            border: '1px dashed #9aa5b1',
            background: '#f8fafc',
            color: '#555',
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          🎤 改用語音輸入條件
        </button>
        {voiceHintShown && (
          <p data-testid="rec-voice-hint" style={{ marginTop: 8, color: '#8a6d3b', fontSize: 13 }}>
            語音輸入將於語音互動模組（C-3）上線後開放；目前請使用上方觸控表單輸入條件。
          </p>
        )}
      </section>

      {recommendMutation.isError && (
        <p className="error-text" data-testid="rec-error">
          推薦失敗：
          {recommendMutation.error instanceof ApiError
            ? (recommendMutation.error.problem?.detail ?? recommendMutation.error.message)
            : '未知錯誤'}
        </p>
      )}

      {result !== undefined && (
        <section style={{ marginTop: 12 }}>
          <h2 style={{ fontSize: 18, margin: '0 0 8px' }}>推薦行程（{result.party_size} 人）</h2>
          {result.recommendations.length === 0 ? (
            <p data-testid="rec-empty" style={{ color: '#777' }}>
              目前沒有符合條件的行程組合，請放寬停留時間或預算後再試。
            </p>
          ) : (
            result.recommendations.map((itin) => (
              <article key={itin.id} className="card" data-testid="rec-card">
                <div data-testid="rec-card-products" style={{ marginBottom: 8 }}>
                  {itin.products.map((p, idx) => (
                    <div key={p.product_id} style={{ fontSize: 15, fontWeight: 600 }}>
                      {idx + 1}. {pickI18n(p.name_i18n, p.sku, locale)}
                      <span style={{ marginLeft: 8, fontSize: 13, color: '#777', fontWeight: 400 }}>
                        {p.duration_minutes} 分鐘
                      </span>
                    </div>
                  ))}
                </div>
                <div className="kv">
                  <span className="kv-label">預估時間</span>
                  <span className="kv-value" data-testid="rec-card-time">
                    {itin.estimated_minutes} 分鐘
                  </span>
                </div>
                <div className="kv">
                  <span className="kv-label">總價</span>
                  <span
                    className="kv-value"
                    data-testid="rec-card-price"
                    style={{ fontSize: 18, fontWeight: 700 }}
                  >
                    NT$ {Math.round(Number(itin.total_price)).toLocaleString('en-US')}
                  </span>
                </div>
                <p style={{ color: '#555', fontSize: 13, margin: '6px 0 12px' }}>{itin.fit_note}</p>
                <button
                  type="button"
                  className="btn-primary btn-block"
                  data-testid={`rec-buy-${itin.id}`}
                  onClick={() => buyItinerary(itin)}
                >
                  一鍵帶入購票
                </button>
              </article>
            ))
          )}
        </section>
      )}
    </div>
  );
}

/** 加減步進器（人數輸入）。 */
function Counter({
  label,
  testId,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  testId: string;
  value: number;
  min: number;
  max: number;
  onChange: (n: number) => void;
}): JSX.Element {
  return (
    <div>
      <label style={{ fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 6 }}>
        {label}
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }} data-testid={testId}>
        <button
          type="button"
          className="qty-step"
          aria-label={`${label}減少`}
          disabled={value <= min}
          onClick={() => onChange(Math.max(min, value - 1))}
        >
          −
        </button>
        <span
          data-testid={`${testId}-value`}
          style={{ fontSize: 16, fontWeight: 600, minWidth: 24, textAlign: 'center' }}
        >
          {value}
        </span>
        <button
          type="button"
          className="qty-step"
          aria-label={`${label}增加`}
          disabled={value >= max}
          onClick={() => onChange(Math.min(max, value + 1))}
        >
          +
        </button>
      </div>
    </div>
  );
}
